<template>
  <div class="offers-table-container">
    <v-data-table
      :headers="headers"
      :search="search"
      :loading="loading"
      :items-per-page="franchisee ? 5 : 10"
      item-key="_id"
      show-select
      :selected-items.sync="selectedItems"
      :single-item="isMariaCampaignFinished"
      :items.sync="items"
    >
      <template v-slot:[`header.data-table-select`]>
        <v-checkbox
          v-if="selectedItems.length"
          :value="isAllSelected"
          :disabled="disabled || isCampaignActivated"
          hide-details
          color="#0079F2"
          indeterminate
          @change="toggleUndoSelection"
        />
      </template>

      <template v-slot:[`item.data-table-select`]="{ item }">
        <mf-tooltip top max-width="350px" :disabled="!item.disabled">
          <template v-slot:activator>
            <v-checkbox
              :input-value="isSelected(item)"
              :disabled="item.disabled || isInitiallyOffers(item)"
              hide-details
              color="#0079F2"
              @change="$emit('update:selected-items', toggleSelection(item))"
            />
          </template>
          <span>{{ disabledOfferTooltip(item) }}</span>
        </mf-tooltip>
      </template>

      <template v-slot:[`item.status`]="{ item }">
        <div class="offers-table-status">
          <mf-icon fill small :color="getStatusLabel(item.status).color">fiber_manual_record</mf-icon><span>{{ getStatusLabel(item.status).text }}</span>
        </div>
      </template>

      <template v-slot:[`item.name`]="{ item }">
        <mf-tooltip top max-width="350px">
          <template v-slot:activator>
            <router-link :class="`offer-link-name ${item.disabled && 'offer-link-name-disabled'}`" target="_blank" :to="redirectToOffer(item)">
              {{ putEllipsis(item.name, 30) }}
            </router-link>
          </template>
          <span>{{ item.name }}</span>
        </mf-tooltip>
      </template>

      <template v-slot:[`item.units`]="{ item }">
        <mf-tooltip v-if="item.units.length > 0" right variant="secondary">
          <template #activator>
            <span>
              {{ getUnits(item) }}
            </span>
          </template>
          <span v-for="(unit, index) in item.units" :key="index" style="display: block">
            {{ unit.name }}
          </span>
        </mf-tooltip>
        <span v-else>-</span>
      </template>

      <template v-slot:[`item.is_personalized`]="{ item }">
        {{ item.segments.length ? 'Personalizado' : 'Clube' }}
      </template>

      <template v-slot:[`item.types`]="{ item }">
        <mf-tooltip v-if="!isMariaCampaignFinished" top max-width="300px" variant="secondary">
          <template #activator>
            <span>{{ putEllipsis(item.typesForSort, 23) }}</span>
          </template>
          <span>{{ getOfferTypesTooltip(item.types) }}</span>
        </mf-tooltip>

        <mf-tooltip v-else top max-width="300px" variant="secondary">
          <template #activator>
            <span>{{ putEllipsis(item.typesForSort, 23) }}</span>
          </template>
          <span>{{ getOfferTypesTooltipBySingleOfferSale(item) }}</span>
        </mf-tooltip>
      </template>

      <template v-slot:[`item.start_date`]="{ item }">
        <mf-tooltip top variant="secondary" :disabled="!showDurationTooltip(item.status)">
          <template #activator>
            <span v-if="showDurationTooltip(item.status)">{{ getPeriod(item.start_date, item.end_date) }}</span>
            <span v-else>{{ getPeriod(item.start_date, item.end_date) }}</span>
          </template>
          <span v-if="showDurationTooltip(item.status)">{{ getCampaignDuration(item) }}</span>
        </mf-tooltip>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { DateTime } from 'luxon'
import { QUERY_LIST_SALES_CAMPAIGNS } from '@/modules/campanhas/gestao-ofertas-v2/graphql'
import { getStatusLabel, getUnits, getPeriod, putEllipsis } from '@/config/helpers.js'
import { getProductFormatTypeLabel } from '@/modules/mobile/ofertas/helpers/productType.js'
import { mariaCampaign } from '../../../mixins/maria-campaign'

const ALLOWED_OFFER_TYPES = [
  'general',
  'takepay',
  'percentage',
  'combo',
  'points',
  'cashback',
  'cashback_percentage',
  'points_offer_sales',
  'cashback_absolute',
]

const TABLE_HEADERS = [
  { text: 'Status', value: 'status', width: '6%' },
  { text: 'Nome', value: 'name', width: '15%' },
  { text: 'Lojas', value: 'units', width: '80px' },
  { text: 'Produtos', value: 'products_count', width: '10%' },
  { text: 'Público-alvo', value: 'is_personalized', width: '10%', sortable: false },
  { text: 'Formato', value: 'typesForSort', width: '15%', sortable: true },
  { text: 'Validade', value: 'start_date', width: '180px' },
]

export default {
  name: 'OffersTable',
  mixins: [mariaCampaign],
  props: {
    disabled: {
      type: Boolean,
      default: false,
    },
    search: {
      type: String,
      default: '',
    },
    franchisee: {
      type: Boolean,
      default: false,
    },
    erpOffers: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    selectedItems: [],
    loading: false,
    isAllSelected: false,
    indeterminateAll: false,
    items: [],
    initiallyOffers: [],
  }),
  computed: {
    queryVariables() {
      return {
        status: ['active', 'scheduled'],
        franchise: this.franchisee,
        units: this.units ? this.units : [],
        includeFlashMode: false,
        includeDateTime: false,
        includeAuto: this.erpOffers,
      }
    },
    headers() {
      return TABLE_HEADERS
    },
    isAllOffersDisabled() {
      const disableAllOffers =
        (!this.franchisee && this.hasFranchiseeOfferSelected) ||
        (this.franchisee && this.hasOfferSelected && !this.hasFranchiseeOfferSelected) ||
        this.isMariaCampaignFinished

      this.disableOffers(disableAllOffers)
      return disableAllOffers
    },
    hasOfferSelected() {
      return this.campaign.offer_schedules.length > 0
    },
    hasNotPersonalizedOfferSelected() {
      return this.selectedItems.some((offer) => !offer.segments.length)
    },
    hasPersonalizedOfferSelected() {
      return this.selectedItems.some((offer) => offer.segments.length > 0)
    },
    isAllOffersDisabledTooltip() {
      return this.franchisee
        ? 'Ao selecionar suas ofertas, as ofertas da rede não poderão ser selecionadas.'
        : 'Ao selecionar ofertas da rede, suas ofertas não poderão ser selecionadas.'
    },
    selectedOffersSchedulesIds() {
      return this.campaign.offer_schedules.map((item) => item._id || item.offer_schedule_id || item?.offer_schedule?._id)
    },
  },
  async mounted() {
    await this.getOffersData()
    if (this.hasOfferSelected) {
      this.setSelectedItemsOnMount()
    } else if (this.itsGetCampaignFromOffers) {
      this.selectOfferFromItems(this.$route.params.offerScheduleId)
    }
  },
  methods: {
    getStatusLabel,
    getUnits,
    getPeriod,
    putEllipsis,
    selectOfferFromItems(offerScheduleId) {
      const itemToSelect = this.items.find((item) => item._id === offerScheduleId)
      if (itemToSelect) {
        this.selectItem(itemToSelect)
        this.setOmniCampaignKey({ key: 'title', payload: itemToSelect.name })
        this.setOmniCampaignKey({ key: 'description', payload: itemToSelect.description })
      }
    },
    getFormattedOffer(offer) {
      return {
        offer_schedule_id: offer._id,
        units: offer.units,
        name: offer.name,
        draft: offer.draft,
        active: offer.active,
        status: offer.status,
        start_date: offer.start_date,
        end_date: offer.end_date,
        segments: offer.segments,
        from_franchise: this.franchisee,
        origin: offer.origin,
        offer_sales: this.getFormattedOfferProducts(offer, 'offer_sales'),
        cashback_absolute_offer_sales: this.getFormattedOfferProducts(offer, 'cashback_absolute_offer_sales'),
        cashback_percentage_offer_sales: this.getFormattedOfferProducts(offer, 'cashback_percentage_offer_sales'),
        combo_offer_sales: this.getFormattedOfferProducts(offer, 'combo_offer_sales'),
        next_buy_cashback_offer_sales: this.getFormattedOfferProducts(offer, 'next_buy_cashback_offer_sales'),
        percentage_offer_sales: this.getFormattedOfferProducts(offer, 'percentage_offer_sales'),
        points_offer_sales: this.getFormattedOfferProducts(offer, 'points_offer_sales'),
        takepay_offer_sales: this.getFormattedOfferProducts(offer, 'takepay_offer_sales'),
      }
    },
    getFormattedOfferProducts(offer, key) {
      if (!offer[key]) {
        return []
      }
      return offer[key]
        .flatMap((product) => {
          if (product?.offer_sales?.length) {
            const offerSalesProduct = product?.offer_sales?.map((productValue) => ({
              ...product,
              group_name: product.name,
              group_id: product._id,
              active: productValue.active,
              name: productValue.name,
              sku: productValue.sku,
              offer_schedule_id: offer._id,
              selectedEans: productValue.eans,
              ean: productValue.ean,
              eans: productValue?.product?.eans,
            }))
            return offerSalesProduct
          }

          if (product?._id) {
            const groupProduct = product[key].map((productValue) => ({
              ...product,
              ...productValue,
              group_name: product.name,
              group_id: product._id,
              selectedEans: product.eans,
              eans: product?.product?.eans,
              group_rules_exhibition: product.group_rules_exhibition,
              offer_schedule_id: offer._id,
            }))

            groupProduct.forEach((product) => {
              delete product[key]
            })

            return groupProduct
          }

          return { ...product, offer_schedule_id: offer._id, selectedEans: product.eans, eans: product?.product?.eans }
        })
        .flat()
    },
    isInitiallyOffers(offer) {
      if (!this.isCampaignActivated) {
        return false
      }
      return this.initiallyOffers.some((initialOffer) => initialOffer._id === offer._id || initialOffer.offer_schedule?._id === offer._id)
    },
    setSelectedItemsOnMount() {
      this.selectedItems = []

      this.campaign.offer_schedules.map((offer) => {
        const offerScheduleId = offer.offer_schedule_id ?? offer?.offer_schedule?._id
        const itemToSelect = this.items.find((item) => item._id === offerScheduleId)
        if (itemToSelect) {
          this.selectItem(itemToSelect)
        }
      })

      this.initiallyOffers = this.campaign.offer_schedules
    },
    disableOffers(disableAll) {
      this.items = this.items.map((offer) => ({
        ...offer,
        disabled: disableAll ? true : this.isOfferDisabled(offer) || this.disabled,
        typesForSort: this.getOfferTypesDisplayText(offer),
      }))
    },
    toggleUndoSelection() {
      this.selectedItems = []
      this.setOmniCampaignKey({ key: 'initial_date', payload: '' })
      this.setOmniCampaignKey({ key: 'final_date', payload: '' })
      this.setOmniCampaignKey({ key: 'offer_schedules', payload: [] })
      this.isAllSelected = false
    },
    selectItem(item) {
      if (!this.isSelected(item)) {
        this.updateSegments(item, false)
        this.updateSelectedItems(item, false)
        this.updateOffersSchedules(item, false)
      }
    },
    toggleSelection(item) {
      const isItemSelected = this.isSelected(item)
      this.updateSegments(item, isItemSelected)
      this.updateSelectedItems(item, isItemSelected)
      this.updateOffersSchedules(item, isItemSelected)
    },
    /**
     * Updates the segments in the omni campaign key
     *
     * If the offer is being selected, we add the segments to the existing segments
     *
     * If the offer is being deselected, we remove the segments from the existing segments,
     * ONLY IF the segment that it has is not present in any other offer
     *
     * If the offer has no segments, we don't update the segments
     *
     * @param {Object} item - The offer item
     * @param {boolean} isItemSelected - Whether the offer is selected
     */
    updateSegments(item, isItemSelected) {
      const segments = item?.segments?.length ? item.segments : []
      if (!segments?.length) {
        // Offer has no segments, no need to update segments
        return
      }
      if (!isItemSelected) {
        // New offer being selected
        const newSegments = [...this.campaign.segments]
        segments.forEach((segment) => {
          if (!newSegments.map((s) => s._id).includes(segment._id)) {
            newSegments.push(segment)
          }
        })
        this.setOmniCampaignKey({ key: 'segments', payload: newSegments })
      } else {
        // Offer being deselected
        const otherOffers = this.campaign.offer_schedules
          .map((offer) => {
            // Loaded up format from previous save
            if (offer.offer_schedule) {
              return {
                _id: offer.offer_schedule._id,
                segments: offer.offer_schedule.segments,
              }
            }
            // Selected now format
            return {
              _id: offer.offer_schedule_id,
              segments: offer.segments,
            }
          })
          .filter((offer) => {
            return offer._id !== item._id
          })
        const otherOfferSegments = otherOffers.filter((offer) => offer.segments?.length).flatMap((offer) => offer.segments)
        const exclusiveSegmentsInRemovedOffer = segments.filter((segment) => !otherOfferSegments.map((s) => s._id).includes(segment._id))
        const newSegments = this.campaign.segments.filter((segment) => !exclusiveSegmentsInRemovedOffer.map((s) => s._id).includes(segment._id))
        this.setOmniCampaignKey({ key: 'segments', payload: newSegments })
      }
    },
    updateSelectedItems(item, isItemSelected) {
      this.selectedItems = isItemSelected ? this.selectedItems.filter((selected) => selected._id !== item._id) : [...this.selectedItems, item]
    },
    updateOffersSchedules(item, isItemSelected) {
      const updatedOffersSchedules = isItemSelected
        ? this.campaign.offer_schedules.filter((schedule) => {
            const idToCompare = item._id
            const scheduleId = schedule.offer_schedule?._id || schedule.offer_schedule_id || schedule._id

            return scheduleId !== idToCompare
          })
        : this.addOfferIfNotExists(this.campaign.offer_schedules, this.getFormattedOffer(item))

      if (!updatedOffersSchedules.length) {
        this.resetDates()
      }
      this.setOmniCampaignKey({ key: 'offer_schedules', payload: updatedOffersSchedules })
    },
    addOfferIfNotExists(existingOffers, newOffer) {
      const offerExists = existingOffers.some((schedule) => {
        const scheduleId = schedule.offer_schedule?._id || schedule.offer_schedule_id || schedule._id
        return scheduleId === newOffer.offer_schedule_id
      })

      return offerExists ? existingOffers : [...existingOffers, newOffer]
    },
    removeOfferFromSchedules(item) {
      return this.campaign.offer_schedules.filter((schedule) => {
        const idToCompare = item._id
        if (schedule.offer_schedule_id) {
          return schedule.offer_schedule_id !== idToCompare
        }
        if (schedule._id) {
          return schedule._id !== idToCompare
        }
        return true
      })
    },
    resetDates() {
      this.setOmniCampaignKey({ key: 'initial_date', payload: '' })
      this.setOmniCampaignKey({ key: 'final_date', payload: '' })
    },
    disabledOfferTooltip(item) {
      if (this.isAllOffersDisabled) {
        return this.isAllOffersDisabledTooltip
      }

      const tooltips = {
        personalized: 'Uma gestão de ofertas sem segmento já foi adicionada. Não é possível adicionar outra gestão de ofertas que possua um segmento.',
        personalizedSelected: 'Uma gestão de ofertas com segmento já foi adicionada. Não é possível adicionar outras gestões de ofertas.',
        notDefault: 'Não é possível selecionar gestões de ofertas com o formato de oferta relâmpago.',
        dynamicPricing: 'Não é possível selecionar gestões de ofertas com o formato de desconto percentual por loja.',
        intelligentDistribution: 'Não é possível selecionar ofertas com o tipo de Distribuição inteligente.',
        industryType: 'Não é possível selecionar gestões de ofertas com esse formato de oferta da indústria.',
        notAllowedType: 'Não é possível selecionar essa modalidade de gestão de ofertas na campanha da mar.ia.',
        dateInterval: 'Não é possível selecionar gestão de ofertas com intervalos maior de 7 dias entre si.',
        default: 'Oferta desabilitada.',
      }

      if (this.disablePersonalized(item)) return tooltips.personalized
      if (this.disableAllOtherOffersWhenPersonalizedOneIsSelected(item)) return tooltips.personalizedSelected
      if (this.disableIfIsNotDefault(item)) return tooltips.notDefault
      if (this.disableDynamicPricingPercentage(item)) return tooltips.dynamicPricing
      if (this.disableIntelligentDistribution(item)) return tooltips.intelligentDistribution
      if (this.disableNotAllowedTypes(item) && item?.industry) return tooltips.industryType
      if (this.disableNotAllowedTypes(item)) return tooltips.notAllowedType
      if (this.disableByDateInterval(item)) return tooltips.dateInterval

      return tooltips.default
    },
    isSelected(item) {
      return this.selectedItems.some((offer) => offer._id === item._id)
    },
    disableIfIsNotDefault(offer) {
      return Boolean(offer?.flash)
    },
    disablePersonalized(offer) {
      return this.hasNotPersonalizedOfferSelected && offer.segments.length > 0
    },
    disableAllOtherOffersWhenPersonalizedOneIsSelected(offer) {
      if (!this.hasPersonalizedOfferSelected) return false
      return !this.selectedItems.some((selected) => selected._id === offer._id)
    },
    isOfferDisabled(offer) {
      return (
        this.disabled ||
        this.disableAllOtherOffersWhenPersonalizedOneIsSelected(offer) ||
        this.disableNotAllowedTypes(offer) ||
        this.disableIfIsNotDefault(offer) ||
        this.disablePersonalized(offer) ||
        this.disableByDateInterval(offer) ||
        this.disableIntelligentDistribution(offer)
      )
    },
    disableByDateInterval(verifyOffer) {
      const start = DateTime.fromISO(verifyOffer.start_date)
      const end = DateTime.fromISO(verifyOffer.end_date)

      return this.selectedItems.some((offer) => {
        const existingStart = DateTime.fromISO(offer.start_date)
        const existingEnd = DateTime.fromISO(offer.end_date)

        const diffEndToNewStart = start.diff(existingEnd, 'days').days
        const diffNewEndToStart = existingStart.diff(end, 'days').days

        return diffEndToNewStart > 7 || diffNewEndToStart > 7
      })
    },
    disableDynamicPricingPercentage(offer) {
      return offer.types.includes('dynamic_pricing_percentage')
    },
    disableIntelligentDistribution(offer) {
      return offer.auto_distributed === true
    },
    disableNotAllowedTypes(offer) {
      return !offer.types.some((type) => ALLOWED_OFFER_TYPES.includes(type))
    },
    async getOffersData() {
      try {
        this.loading = true
        const {
          data: { offerSchedules },
        } = await this.$apollo.query({
          query: QUERY_LIST_SALES_CAMPAIGNS,
          variables: this.queryVariables,
          context: {
            uri: this.$microservicesUrls['sales'],
          },
          fetchPolicy: 'no-cache',
        })

        this.items = offerSchedules?.offerSchedules?.map((offer) => ({
          ...offer,
          disabled: this.isOfferDisabled(offer) || this.disabled,
          typesForSort: this.getOfferTypesDisplayText(offer),
        }))
      } catch (error) {
        console.error('Failed to get offer schedules data', error)
      } finally {
        this.loading = false
      }
    },
    showDurationTooltip(status) {
      return ['active', 'scheduled'].includes(status)
    },
    getCampaignDuration(item) {
      const now = DateTime.now()

      if (item.status === 'active') {
        const { days: endCampaign } = DateTime.fromISO(item.end_date).diff(now, ['days']).toObject()
        const formattedEndCampaign = parseInt(endCampaign)
        if (formattedEndCampaign === 0) {
          return 'A oferta termina hoje'
        } else {
          return `A oferta terminará em ${formattedEndCampaign} dia(s)`
        }
      }

      if (item.status === 'scheduled') {
        const { days: initCampaign } = DateTime.fromISO(item.start_date).diff(now, ['days']).toObject()
        const formattedInitCampaign = parseInt(initCampaign)
        if (formattedInitCampaign === 0) {
          return 'A oferta já está agendada para hoje'
        } else {
          return `A oferta iniciará em ${formattedInitCampaign} dia(s)`
        }
      }
    },
    getOfferTypes(types) {
      return types.map((type) => getProductFormatTypeLabel(type))
    },
    getOfferTypesTooltip(types) {
      return this.getOfferTypes(types).join(', ')
    },
    getOfferTypesTooltipBySingleOfferSale(campaign) {
      const types = this.getOfferTypesBySingleOfferSale(campaign)
      return this.getOfferTypes(types).join(', ')
    },
    getOfferTypesTextBySingleOfferSale(campaign) {
      const types = this.getOfferTypesBySingleOfferSale(campaign)
      return types.length > 1 ? 'Múltiplas' : this.getOfferTypes(types)[0]
    },
    getOfferTypesBySingleOfferSale(campaign) {
      const types = []
      const offerTypes = {
        offer_sales: 'general',
        combo_offer_sales: 'combo',
        percentage_offer_sales: 'percentage',
        takepay_offer_sales: 'takepay',
        cashback_percentage_offer_sales: 'cashback_percentage',
        cashback_absolute_offer_sales: 'cashback_absolute',
        points_offer_sales: 'points_offer_sales',
      }

      Object.entries(offerTypes).forEach(([key, value]) => {
        if (campaign[key]?.length) {
          types.push(value)
        }
      })

      return types
    },
    getOfferTypesDisplayText(item) {
      if (!this.isMariaCampaignFinished) {
        const types = this.getOfferTypes(item.types)
        return types.length === 1 ? types[0] : 'Múltiplas'
      } else {
        return this.getOfferTypesTextBySingleOfferSale(item)
      }
    },
    redirectToOffer(offer) {
      const baseUrl = offer.industry ? '/industria/ofertas-trade/visualizar' : '/campanhas/gestao-ofertas'

      return `${baseUrl}/${offer._id}?franchise=${this.franchisee}`
    },
  },
}
</script>

<style lang="scss" scoped>
.offers-table-container {
  margin-top: 20px;
  .offers-table-status {
    display: flex;
    flex-direction: row;
    align-content: center;
    gap: 6px;
  }
  .offer-link-name {
    color: $marca-0-l40;
    text-overflow: ellipsis;
    font-size: 14px;
    font-style: normal;
    font-weight: 700;
    line-height: 150%;
    text-decoration: none;
    &-disabled {
      color: $cinza-l10;
    }
  }
  ::v-deep thead tr th {
    background-color: $cinza-l80;
  }
}
::v-deep thead tr th:first-child,
::v-deep tbody tr td:first-child {
  max-width: 50px !important;
  padding: 0;
}

.offers-table-container ::v-deep .v-input--selection-controls {
  margin: 0 !important;
}

::v-deep .v-input__slot {
  display: flex !important;
  align-content: center !important;
  justify-content: center !important;
}

// paginated-data-table style
::v-deep th {
  color: $cinza-d20 !important;
  font-size: 14px;
  font-weight: bold;
}

::v-deep tbody {
  td {
    color: $cinza-0;
    font-size: 14px;
    line-height: 1;
  }
}
.is-disabled {
  opacity: 0.5;
  pointer-events: none;
  ::v-deep th {
    color: grey !important;
  }

  ::v-deep tbody td {
    color: grey !important;
  }
}
</style>
